import React, { useState } from 'react';
import { X, Database, CheckCircle, AlertCircle, Loader, Info } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

// Fallback copy icon to avoid version mismatches in lucide-react
const CopyIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
import axios from 'axios';

const AddDatasourceModal = ({ isOpen, onClose, onDatasourceAdded, user, onOpenFilePanel }) => {
  const [step, setStep] = useState('select'); // 'select', 'configure'
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pythonPath, setPythonPath] = useState('');
  const [pyodbcDrivers, setPyodbcDrivers] = useState(null);
  const [showLocalTestHelpModal, setShowLocalTestHelpModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSuggestedModal, setShowSuggestedModal] = useState(false);
  const [suggestedString, setSuggestedString] = useState('');
  const [suggestedCopied, setSuggestedCopied] = useState(false);
  const [mssqlCopied, setMssqlCopied] = useState(false);

  // Privacy field (for admin/owner)
  const [privacy, setPrivacy] = useState('private');

  // Read-only mode (default true for safety)
  const [readOnly, setReadOnly] = useState(true);

  // Connection failure confirmation modal
  const [showConnectionFailureModal, setShowConnectionFailureModal] = useState(false);
  const [connectionFailureMessage, setConnectionFailureMessage] = useState('');
  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  // PostgreSQL fields
  const [postgresConnectionString, setPostgresConnectionString] = useState('');

  // MySQL fields
  const [mysqlInputMode, setMysqlInputMode] = useState('string'); // 'individual' or 'string'
  const [mysqlConnectionString, setMysqlConnectionString] = useState('');
  const [mysqlHost, setMysqlHost] = useState('');
  const [mysqlPort, setMysqlPort] = useState('3306');
  const [mysqlDatabase, setMysqlDatabase] = useState('');
  const [mysqlUser, setMysqlUser] = useState('');
  const [mysqlPassword, setMysqlPassword] = useState('');
  // MSSQL fields
  const [mssqlConnectionString, setMssqlConnectionString] = useState('');

  // Snowflake fields
  const [snowflakeConnectionString, setSnowflakeConnectionString] = useState('');

  // IP Whitelist section
  const [showIpWhitelist, setShowIpWhitelist] = useState(false);
  const [ipAddresses, setIpAddresses] = useState({ local: null, server: null });
  const [ipLoading, setIpLoading] = useState(false);
  const [ipCopied, setIpCopied] = useState({ local: false, server: false });

  const resetForm = () => {
    setStep('select');
    setSelectedType(null);
    setName('');
    setTesting(false);
    setTestResult(null);
    setSaving(false);
    setPrivacy('private');
    setReadOnly(true);
    setPostgresConnectionString('');
    setMysqlInputMode('string');
    setMysqlConnectionString('');
    setMysqlHost('');
    setMysqlPort('3306');
    setMysqlDatabase('');
    setMysqlUser('');
    setMysqlPassword('');
    setMssqlConnectionString('');
    setSnowflakeConnectionString('');
    setShowConnectionFailureModal(false);
    setConnectionFailureMessage('');
    setPendingSavePayload(null);
    setShowIpWhitelist(false);
    setIpAddresses({ local: null, server: null });
    setIpCopied({ local: false, server: false });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Close modals on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showConnectionFailureModal) {
          setShowConnectionFailureModal(false);
          setSaving(false);
        } else if (showLocalTestHelpModal) {
          setShowLocalTestHelpModal(false);
        } else if (showSuggestedModal) {
          setShowSuggestedModal(false);
        } else {
          handleClose();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, showLocalTestHelpModal, showSuggestedModal, showConnectionFailureModal]);

  // Fetch backend env info for building a local test command
  React.useEffect(() => {
    if (!isOpen) return;
    const fetchEnvInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API_ENDPOINTS.main}/datasources/env-info`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setPythonPath(resp.data?.python_executable || 'python');
        setPyodbcDrivers(resp.data?.pyodbc_drivers || null);
      } catch (e) {
        setPythonPath('python');
        setPyodbcDrivers(null);
      }
    };
    fetchEnvInfo();
  }, [isOpen]);

  // Fetch IP addresses for whitelisting
  const fetchIpAddresses = async () => {
    if (ipAddresses.local && ipAddresses.server) return; // Already fetched

    setIpLoading(true);
    try {
      // Fetch local IP from public API
      const localIpPromise = fetch('https://api.ipify.org?format=json')
        .then(r => r.json())
        .then(d => d.ip)
        .catch(() => null);

      // Fetch server IP from backend
      const token = localStorage.getItem('token');
      const serverIpPromise = axios.get(`${API_ENDPOINTS.main}/system/outbound-ip`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }).then(r => r.data?.ip).catch(() => null);

      const [localIp, serverIp] = await Promise.all([localIpPromise, serverIpPromise]);

      setIpAddresses({
        local: localIp,
        server: serverIp
      });
    } catch (e) {
      console.error('Failed to fetch IPs:', e);
    } finally {
      setIpLoading(false);
    }
  };

  const handleCopyIp = (type, ip) => {
    navigator.clipboard.writeText(ip);
    setIpCopied(prev => ({ ...prev, [type]: true }));
    setTimeout(() => setIpCopied(prev => ({ ...prev, [type]: false })), 2000);
  };

  const buildLocalTestCommand = () => {
    const py = pythonPath || 'python';
    if (selectedType === 'postgresql') {
      const s = postgresConnectionString || '';
      return `${py} -c "import sys, psycopg2; print('Python:', sys.executable); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; c=psycopg2.connect(s); print('OK'); c.close()"`;
    }
    if (selectedType === 'mysql') {
      if (mysqlInputMode === 'string') {
        const s = mysqlConnectionString || '';
        return `${py} -c "import sys, pymysql, urllib.parse as u; print('Python:', sys.executable); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; p=u.urlparse(s); c=pymysql.connect(host=p.hostname or 'localhost', port=p.port or 3306, user=u.unquote(p.username or ''), password=u.unquote(p.password or ''), database=(p.path or '/').lstrip('/')); print('OK'); c.close()"`;
      }
      return `${py} -c "import sys, pymysql; print('Python:', sys.executable); c=pymysql.connect(host='${mysqlHost}', port=${parseInt(mysqlPort||'3306')}, user='${mysqlUser}', password='${mysqlPassword}', database='${mysqlDatabase}'); print('OK'); c.close()"`;
    }
    if (selectedType === 'mssql') {
      const s = mssqlConnectionString || '';
      return `${py} -c "import sys, pyodbc; print('Python:', sys.executable); print('Drivers:', pyodbc.drivers()); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; c=pyodbc.connect(s); print('OK'); c.close()"`;
    }
    if (selectedType === 'snowflake') {
      const s = snowflakeConnectionString || '';
      return `${py} -c "import sys; from urllib.parse import urlparse, parse_qs, unquote; import snowflake.connector; print('Python:', sys.executable); u=urlparse(r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'); p=u.path.lstrip('/').split('/'); q=parse_qs(u.query); c=snowflake.connector.connect(account=u.hostname, user=unquote(u.username or ''), password=unquote(u.password or ''), database=p[0] if p else None, schema=p[1] if len(p)>1 else None, warehouse=q.get('warehouse',[''])[0] or None, role=q.get('role',[''])[0] or None); print('OK'); c.close()"`;
    }
    return '';
  };

  const buildPythonSuggestedSnippet = () => {
    const s = suggestedString || '';
    const safe = s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\"/g, '\\"');
    return `import pyodbc\nconn = pyodbc.connect(r'${safe}')`;
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    const nameLabel = type === 'postgresql' ? 'PostgreSQL' : type === 'mysql' ? 'MySQL' : type === 'snowflake' ? 'Snowflake' : 'MSSQL';
    setName(`My ${nameLabel} Database`);
    setStep('configure');
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        db_type: selectedType
      };

      if (selectedType === 'postgresql') {
        payload.connection_string = postgresConnectionString;
      } else if (selectedType === 'mysql') {
        if (mysqlInputMode === 'string') {
          payload.connection_string = mysqlConnectionString;
        } else {
          payload.host = mysqlHost;
          payload.port = parseInt(mysqlPort);
          payload.database = mysqlDatabase;
          payload.user = mysqlUser;
          payload.password = mysqlPassword;
        }
      } else if (selectedType === 'mssql') {
        payload.connection_string = mssqlConnectionString;
      } else if (selectedType === 'snowflake') {
        payload.connection_string = snowflakeConnectionString;
      }

      const url = `${API_ENDPOINTS.main}/datasources/test`;
      console.log('Testing connection with payload:', { ...payload, password: '***' });
      console.log('Full URL:', url);
      console.log('Token:', token ? 'Present' : 'Missing');

      const response = await axios.post(
        url,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );

      console.log('Test response:', response.data);
      setTestResult({
        success: response.data.success,
        message: response.data.message,
        suggested_connection_string: response.data.suggested_connection_string || null
      });

      // Auto-dismiss success message after 5 seconds
      if (response.data.success) {
        setTimeout(() => setTestResult(null), 5000);
      }

      // If MSSQL test succeeded with a suggested string, apply it, store it, and show modal
      if (response.data.success && response.data.suggested_connection_string && selectedType === 'mssql') {
        setMssqlConnectionString(response.data.suggested_connection_string);
        setSuggestedString(response.data.suggested_connection_string);
        setShowSuggestedModal(true);
      }
    } catch (error) {
      console.error('Test connection error:', error);
      let errorMessage = 'Connection test failed';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out after 30 seconds';
      } else if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        errorMessage = error.message || 'Connection test failed';
      }

      setTestResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    // Build payload
    const payload = {
      name: name,
      db_type: selectedType,
      privacy: privacy,
      read_only: readOnly
    };

    if (selectedType === 'postgresql') {
      payload.connection_string = postgresConnectionString;
    } else if (selectedType === 'mysql') {
      if (mysqlInputMode === 'string') {
        payload.connection_string = mysqlConnectionString;
      } else {
        if (!mysqlHost || !mysqlDatabase || !mysqlUser) {
          alert('MySQL host, database, and user are required');
          return;
        }
        payload.host = mysqlHost;
        payload.port = parseInt(mysqlPort);
        payload.database = mysqlDatabase;
        payload.user = mysqlUser;
        payload.password = mysqlPassword;
      }
    } else if (selectedType === 'mssql') {
      payload.connection_string = mssqlConnectionString;
    } else if (selectedType === 'snowflake') {
      if (!snowflakeConnectionString) {
        alert('Snowflake connection string is required');
        return;
      }
      payload.connection_string = snowflakeConnectionString;
    }

    // If user already tested successfully, save directly
    if (testResult?.success) {
      setSaving(true);
      await saveDatasource(payload);
      return;
    }

    // Otherwise, test connection first
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const testPayload = { db_type: selectedType };

      if (selectedType === 'postgresql') {
        testPayload.connection_string = postgresConnectionString;
      } else if (selectedType === 'mysql') {
        if (mysqlInputMode === 'string') {
          testPayload.connection_string = mysqlConnectionString;
        } else {
          testPayload.host = mysqlHost;
          testPayload.port = parseInt(mysqlPort);
          testPayload.database = mysqlDatabase;
          testPayload.user = mysqlUser;
          testPayload.password = mysqlPassword;
        }
      } else if (selectedType === 'mssql') {
        testPayload.connection_string = mssqlConnectionString;
      } else if (selectedType === 'snowflake') {
        testPayload.connection_string = snowflakeConnectionString;
      }

      const testResponse = await axios.post(
        `${API_ENDPOINTS.main}/datasources/test`,
        testPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );

      // Connection successful - save directly
      if (testResponse.data.success) {
        await saveDatasource(payload);
      } else {
        // Connection failed but backend responded - show modal
        setConnectionFailureMessage(testResponse.data.message || 'Connection test failed');
        setPendingSavePayload(payload);
        setShowConnectionFailureModal(true);
        setSaving(false);
      }
    } catch (error) {
      // Connection failed - ask user if they want to save anyway
      let errorMessage = 'Connection test failed';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out after 30 seconds';
      } else if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. The database may be unreachable from this environment.';
      } else {
        errorMessage = error.message || 'Connection test failed';
      }

      setConnectionFailureMessage(errorMessage);
      setPendingSavePayload(payload);
      setShowConnectionFailureModal(true);
      setSaving(false);
    }
  };

  const saveDatasource = async (payload, skipConnectionTest = false) => {
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_ENDPOINTS.main}/datasources/`,
        { ...payload, skip_connection_test: skipConnectionTest },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Clear Taurus config cache (fire-and-forget) - ensures fresh credentials are used
      // This is critical when adding/updating datasources with new passwords
      fetch(`${API_ENDPOINTS.taurus}/api/clear-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Fire-and-forget, don't block on errors

      // Notify parent component
      if (onDatasourceAdded) {
        onDatasourceAdded(response.data);
      }

      // Close modal
      handleClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save datasource');
      setSaving(false);
    }
  };

  const handleSaveAnyway = async () => {
    setShowConnectionFailureModal(false);
    setSaving(true);
    await saveDatasource(pendingSavePayload, true); // skip_connection_test = true
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {step === 'select' ? 'Add New Datasource' : `Configure ${selectedType === 'postgresql' ? 'PostgreSQL' : selectedType === 'mysql' ? 'MySQL' : selectedType === 'snowflake' ? 'Snowflake' : 'MSSQL'} Connection`}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start querying your data with VerbaGPT
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {/* PostgreSQL Card */}
                <button
                  onClick={() => handleSelectType('postgresql')}
                  className="p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left group"
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 gap-2">
                    <Database className="h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <span className="text-base font-medium text-gray-900 dark:text-white">PostgreSQL</span>
                  </div>
                </button>

                {/* MySQL Card */}
                <button
                  onClick={() => handleSelectType('mysql')}
                  className="p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left group"
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 gap-2">
                    <Database className="h-8 w-8 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <span className="text-base font-medium text-gray-900 dark:text-white">MySQL</span>
                  </div>
                </button>

                {/* MSSQL Card */}
                <button
                  onClick={() => handleSelectType('mssql')}
                  className="p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left group"
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 gap-2">
                    <Database className="h-8 w-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                    <span className="text-base font-medium text-gray-900 dark:text-white">MSSQL</span>
                  </div>
                </button>

                {/* Snowflake Card */}
                <button
                  onClick={() => handleSelectType('snowflake')}
                  className="p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left group"
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 gap-2">
                    <Database className="h-8 w-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                    <span className="text-base font-medium text-gray-900 dark:text-white">Snowflake</span>
                  </div>
                </button>
              </div>

              {/* File formats link */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    handleClose();
                    onOpenFilePanel?.();
                  }}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  Looking to add CSV, Excel, JSON, PDF, Word, PowerPoint, TXT, or Markdown files? <span className="underline">Click here</span>
                </button>
              </div>
            </div>
          )}

          {step === 'configure' && (
            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Connection Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="My Database"
                />
              </div>

              {/* Privacy + Access Mode Row */}
              <div className="flex gap-4">
                {/* Privacy Field (for admin/owner only) */}
                {user && (user.role === 'admin' || user.role === 'owner') && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Privacy
                    </label>
                    <select
                      value={privacy}
                      onChange={(e) => setPrivacy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="private">Private</option>
                      <option value="published">
                        {user.role === 'owner' ? 'Publish (Demo)' : 'Publish (Org)'}
                      </option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {privacy === 'private' ? 'Only visible to you' : user.role === 'owner' ? 'Visible to all users' : 'Visible to your org'}
                    </p>
                  </div>
                )}

                {/* Read-Only Access Mode */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Access Mode
                  </label>
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="readOnly"
                        checked={readOnly}
                        onChange={() => setReadOnly(true)}
                        className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      />
                      <span className="ml-1.5 text-sm text-gray-700 dark:text-gray-300">Read-only</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="readOnly"
                        checked={!readOnly}
                        onChange={() => setReadOnly(false)}
                        className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      />
                      <span className="ml-1.5 text-sm text-gray-700 dark:text-gray-300">Full access</span>
                    </label>
                  </div>
                  <p className={`mt-1 text-xs ${readOnly ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {readOnly
                      ? '🔒 Blocks INSERT, UPDATE, DELETE, DROP, and other write operations.'
                      : '⚠️ Allows data modification. Use with caution on production databases.'}
                  </p>
                </div>
              </div>

              {/* PostgreSQL Configuration */}
              {selectedType === 'postgresql' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Connection String
                  </label>
                  <input
                    type="text"
                    value={postgresConnectionString}
                    onChange={(e) => setPostgresConnectionString(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="postgresql://user:password@host:port/database"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Format: postgresql://username:password@hostname:port/database
                  </p>
                </div>
              )}

              {/* MSSQL Configuration */}
              {selectedType === 'mssql' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      MSSQL Connection String
                    </label>
                    {Boolean(suggestedString) && (
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(mssqlConnectionString || ''); setMssqlCopied(true); setTimeout(() => setMssqlCopied(false), 1500); }}
                        className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                        title="Copy successful string"
                      >
                        {mssqlCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={mssqlConnectionString}
                    onChange={(e) => setMssqlConnectionString(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="mssql://username:password@hostname:1433/database"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <strong>URL Format (Recommended):</strong> mssql://username:password@hostname:1433/database<br/>
                    <span className="text-gray-400 dark:text-gray-500">ODBC Format: Driver={'{ODBC Driver 17 for SQL Server}'};Server=...;Database=...;Uid=...;Pwd=...</span>
                  </p>
                </div>
              )}

              {/* MySQL Configuration */}
              {selectedType === 'mysql' && (
                <div className="space-y-4">
                  {/* Input Mode Toggle */}
                  <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setMysqlInputMode('string')}
                      className={`pb-2 px-1 ${
                        mysqlInputMode === 'string'
                          ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      Connection String
                    </button>
                    <button
                      onClick={() => setMysqlInputMode('individual')}
                      className={`pb-2 px-1 ${
                        mysqlInputMode === 'individual'
                          ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      Individual Parameters
                    </button>
                  </div>

                  {mysqlInputMode === 'string' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Connection String
                      </label>
                      <input
                        type="text"
                        value={mysqlConnectionString}
                        onChange={(e) => setMysqlConnectionString(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="mysql://user:password@host:port/database"
                      />
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Format: mysql://username:password@hostname:port/database
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Host
                        </label>
                        <input
                          type="text"
                          value={mysqlHost}
                          onChange={(e) => setMysqlHost(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="localhost or sql.example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Port
                        </label>
                        <input
                          type="number"
                          value={mysqlPort}
                          onChange={(e) => setMysqlPort(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="3306"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Database
                        </label>
                        <input
                          type="text"
                          value={mysqlDatabase}
                          onChange={(e) => setMysqlDatabase(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="my_database"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          User
                        </label>
                        <input
                          type="text"
                          value={mysqlUser}
                          onChange={(e) => setMysqlUser(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="username"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          value={mysqlPassword}
                          onChange={(e) => setMysqlPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Snowflake Configuration */}
              {selectedType === 'snowflake' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Connection String
                  </label>
                  <input
                    type="text"
                    value={snowflakeConnectionString}
                    onChange={(e) => setSnowflakeConnectionString(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="snowflake://user:password@account/database/schema?warehouse=WH"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Format: snowflake://username:password@account/database/schema?warehouse=WAREHOUSE
                  </p>
                </div>
              )}

              {/* Test Result - Fixed position notification */}
              {testResult && (
                <div className="fixed top-4 right-4 z-50 max-w-md animate-slide-in">
                  <div className={`p-4 rounded-lg shadow-lg ${testResult.success ? 'bg-green-50 dark:bg-green-900/90 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-700'}`}>
                    <div className="flex items-start">
                      {testResult.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="ml-3 flex-1">
                        <p className={`text-sm font-medium ${testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                          {testResult.message}
                        </p>
                      </div>
                      <button
                        onClick={() => setTestResult(null)}
                        className={`ml-3 flex-shrink-0 ${testResult.success ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300' : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Help & Tips Button */}
              {selectedType && (
                <div className="mt-2 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setShowLocalTestHelpModal(true)}
                    className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <Info className="h-4 w-4 mr-2" />
                    Help & Tips
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowIpWhitelist(!showIpWhitelist);
                      if (!showIpWhitelist) fetchIpAddresses();
                    }}
                    className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <svg className={`h-4 w-4 mr-1 transition-transform ${showIpWhitelist ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    IP Whitelist
                  </button>
                </div>
              )}

              {/* IP Whitelist Section */}
              {showIpWhitelist && selectedType && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Add these IPs to your database firewall/network policy:
                  </p>
                  {ipLoading ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Fetching IP addresses...
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Local IP (Taurus) */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Your IP:</span>
                          <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
                            {ipAddresses.local || 'Unable to detect'}
                          </code>
                        </div>
                        {ipAddresses.local && (
                          <button
                            type="button"
                            onClick={() => handleCopyIp('local', ipAddresses.local)}
                            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700"
                          >
                            {ipCopied.local ? (
                              <><CheckCircle className="h-3 w-3" /> Copied</>
                            ) : (
                              <><CopyIcon className="h-3 w-3" /> Copy</>
                            )}
                          </button>
                        )}
                      </div>
                      {/* Server IP (Virgo/Railway) */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Server IP:</span>
                          <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
                            {ipAddresses.server || 'Same as above (local mode)'}
                          </code>
                        </div>
                        {ipAddresses.server && ipAddresses.server !== ipAddresses.local && (
                          <button
                            type="button"
                            onClick={() => handleCopyIp('server', ipAddresses.server)}
                            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700"
                          >
                            {ipCopied.server ? (
                              <><CheckCircle className="h-3 w-3" /> Copied</>
                            ) : (
                              <><CopyIcon className="h-3 w-3" /> Copy</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Help & Tips Modal */}
              {showLocalTestHelpModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connection Setup Guide</h3>
                          <button onClick={() => setShowLocalTestHelpModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-4 text-sm text-gray-700 dark:text-gray-300 space-y-4">
                          {selectedType === 'postgresql' && (
                            <div>
                              <strong className="block mb-2 text-base">PostgreSQL Connection String</strong>
                              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-3">
                                <code className="text-xs font-mono text-blue-900 dark:text-blue-200">
                                  postgresql://username:password@hostname:5432/database
                                </code>
                              </div>
                              <ul className="list-disc ml-5 space-y-2">
                                <li><strong>Format:</strong> Replace <code>username</code>, <code>password</code>, <code>hostname</code>, and <code>database</code> with your actual credentials</li>
                                <li><strong>Special characters in password?</strong> URL-encode them (e.g., <code>@</code> becomes <code>%40</code>, <code>#</code> becomes <code>%23</code>)</li>
                                <li><strong>Cloud databases:</strong> Use the connection string from your provider (Supabase, AWS RDS, etc.)
                                  <div className="mt-1 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded font-mono">
                                    Example: postgresql://user:pass@aws-0-us-east-1.pooler.supabase.com:5432/postgres
                                  </div>
                                </li>
                                <li><strong>SSL required?</strong> Add <code>?sslmode=require</code> at the end of the URL</li>
                                <li><strong>Common issues:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>"Connection refused" → Verify hostname and port are correct</li>
                                    <li>"Authentication failed" → Double-check username and password</li>
                                    <li>"Database does not exist" → Ensure database name is spelled correctly</li>
                                    <li>"Timeout" → Check firewall allows connections to port 5432</li>
                                  </ul>
                                </li>
                              </ul>
                            </div>
                          )}
                          {selectedType === 'mysql' && (
                            <div>
                              <strong className="block mb-2 text-base">MySQL Connection String</strong>
                              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3 mb-3">
                                <code className="text-xs font-mono text-orange-900 dark:text-orange-200">
                                  mysql://username:password@hostname:3306/database
                                </code>
                              </div>
                              <ul className="list-disc ml-5 space-y-2">
                                <li><strong>Format:</strong> Replace <code>username</code>, <code>password</code>, <code>hostname</code>, and <code>database</code> with your actual credentials</li>
                                <li><strong>Special characters in password?</strong> URL-encode them (e.g., <code>@</code> becomes <code>%40</code>, <code>#</code> becomes <code>%23</code>)</li>
                                <li><strong>Default port:</strong> MySQL uses port 3306 by default (change if your server uses a different port)</li>
                                <li><strong>Cloud databases:</strong> Use the connection string from your provider (PlanetScale, AWS RDS, etc.)</li>
                                <li><strong>Common issues:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>"Access denied" → Verify username, password, and host permissions</li>
                                    <li>"Unknown database" → Database names are case-sensitive on Linux servers</li>
                                    <li>"Can't connect to MySQL server" → Check hostname and port</li>
                                    <li>"Host is not allowed to connect" → Database user may need permissions for remote connections</li>
                                  </ul>
                                </li>
                              </ul>
                            </div>
                          )}
                          {selectedType === 'mssql' && (
                            <div>
                              <strong className="block mb-2 text-base">MSSQL Connection String</strong>
                              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3 mb-3">
                                <div className="text-xs font-mono text-purple-900 dark:text-purple-200 space-y-2">
                                  <div className="font-bold text-green-700 dark:text-green-300">⭐ Recommended (URL Format):</div>
                                  <div>mssql://username:password@hostname:1433/database</div>
                                  <div className="mt-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                                    <div className="font-bold mb-1">Alternative (ODBC Format):</div>
                                    <div>Driver={'{ODBC Driver 17 for SQL Server}'};Server=HOST,PORT;Database=DB;Uid=USER;Pwd=PASS;</div>
                                  </div>
                                </div>
                              </div>
                              <ul className="list-disc ml-5 space-y-2">
                                <li><strong>URL Format (Recommended):</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li><strong>Simpler and cleaner</strong> - Easy to read and edit</li>
                                    <li><strong>Format:</strong> <code>mssql://username:password@hostname:1433/database</code></li>
                                    <li><strong>Azure SQL:</strong> <code>mssql://user@server:password@server.database.windows.net:1433/mydb</code></li>
                                    <li><strong>AWS RDS:</strong> <code>mssql://admin:password@mydb.xxxxxx.us-east-1.rds.amazonaws.com:1433/mydb</code></li>
                                    <li><strong>On-premises:</strong> <code>mssql://sa:MyPassword@192.168.1.100:1433/MyDatabase</code></li>
                                  </ul>
                                </li>
                                <li><strong>Cloud databases (Most Common):</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li><strong>Azure SQL Database:</strong> Server format is <code>yourserver.database.windows.net</code></li>
                                    <li><strong>Azure username:</strong> Often requires <code>username@servername</code> format</li>
                                    <li><strong>Azure firewall:</strong> Make sure server IP is allowlisted in Azure portal (Networking settings)</li>
                                    <li><strong>AWS RDS for SQL Server:</strong> Use the endpoint from AWS console (e.g., <code>mydb.xxxxxx.us-east-1.rds.amazonaws.com</code>)</li>
                                  </ul>
                                </li>
                                <li><strong>Authentication:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li><strong>SQL Authentication (Most Common):</strong> Use <code>Uid=username;Pwd=password;</code></li>
                                    <li><strong>Windows Authentication (On-Prem Only):</strong> Use <code>Trusted_Connection=yes;</code> - won't work from cloud servers</li>
                                  </ul>
                                </li>
                                <li><strong>On-premises SQL Server:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>Named instances (e.g., <code>HOSTNAME\SQLEXPRESS</code>) require SQL Server Browser service</li>
                                    <li>Prefer explicit port format: <code>HOSTNAME,1433</code> for better reliability</li>
                                    <li>Default port is 1433 (but can vary by configuration)</li>
                                  </ul>
                                </li>
                                {Array.isArray(pyodbcDrivers) && pyodbcDrivers.length > 0 && !pyodbcDrivers.includes('ODBC Driver 17 for SQL Server') && (
                                  <li className="text-amber-700 dark:text-amber-400">
                                    <strong>Available drivers on this server:</strong> {pyodbcDrivers.join(', ')}
                                    <div className="mt-1">Try using one of these drivers in your connection string instead of "ODBC Driver 17 for SQL Server"</div>
                                  </li>
                                )}
                                <li><strong>Common issues:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>"Login failed" → Check username/password (Azure requires username@servername format)</li>
                                    <li>"Cannot open server" → Verify server hostname and check firewall rules (especially for Azure/AWS)</li>
                                    <li>"Client with IP address is not allowed" → Allowlist server IP in cloud provider's firewall settings</li>
                                    <li>"Driver not found" → The ODBC driver specified may not match available drivers on this server</li>
                                  </ul>
                                </li>
                              </ul>
                            </div>
                          )}
                          {selectedType === 'snowflake' && (
                            <div>
                              <strong className="block mb-2 text-base">Snowflake Connection String</strong>
                              <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-md p-3 mb-3">
                                <div className="text-xs font-mono text-cyan-900 dark:text-cyan-200 space-y-2">
                                  <div className="font-bold">Password Auth:</div>
                                  <div>snowflake://user:password@account/database/schema?warehouse=WH</div>
                                  <div className="mt-2 pt-2 border-t border-cyan-200 dark:border-cyan-700">
                                    <div className="font-bold">Token Auth (PAT):</div>
                                    <div>snowflake://user@account/database/schema?warehouse=WH&token=YOUR_TOKEN</div>
                                  </div>
                                </div>
                              </div>
                              <ul className="list-disc ml-5 space-y-2">
                                <li><strong>Programmatic Access Token (PAT):</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>Generate in Snowflake: Settings → Authentication → Programmatic Access Tokens</li>
                                    <li>Use <code>&token=YOUR_TOKEN</code> in the connection string</li>
                                    <li>No password needed with PAT authentication</li>
                                  </ul>
                                </li>
                                <li><strong>Finding your Account Identifier:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>Look at your Snowflake URL: <code>https://[account].snowflakecomputing.com</code></li>
                                    <li>The account part goes after the <code>@</code> symbol</li>
                                    <li>Include region if present: <code>xy12345.us-east-1</code></li>
                                  </ul>
                                </li>
                                <li><strong>URL Parts:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li><code>user</code> → Your Snowflake username</li>
                                    <li><code>account</code> → Account identifier (e.g., <code>xy12345.us-east-1</code>)</li>
                                    <li><code>/database/schema</code> → Database and schema to connect to</li>
                                    <li><code>?warehouse=WH</code> → Warehouse for compute (required)</li>
                                    <li><code>&role=ROLE</code> → Optional: specify a role</li>
                                  </ul>
                                </li>
                                <li><strong>Common issues:</strong>
                                  <ul className="list-circle ml-5 mt-1 space-y-1">
                                    <li>"Incorrect username or password" → Verify credentials in Snowflake</li>
                                    <li>"Account not found" → Check account identifier (may need region)</li>
                                    <li>"Warehouse does not exist" → Verify warehouse name</li>
                                  </ul>
                                </li>
                              </ul>
                            </div>
                          )}

                          {/* General Tips Section */}
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                            <strong className="block mb-2 text-base">💡 Testing Your Connection</strong>
                            <ul className="list-disc ml-5 space-y-2">
                              <li><strong>Click "Test Connection"</strong> to verify the app can connect to your database</li>
                              <li><strong>If test fails:</strong> Double-check your connection string format and credentials</li>
                              <li><strong>Connection timeout?</strong> Your database may not be accessible from this server due to:
                                <ul className="list-circle ml-5 mt-1 space-y-1">
                                  <li>Firewall blocking incoming connections</li>
                                  <li>Database only accepting local connections (localhost/127.0.0.1)</li>
                                  <li>VPN or SSH tunnel required for remote access</li>
                                </ul>
                              </li>
                              <li><strong>Want to save anyway?</strong> If the test fails but you know the connection works in your environment, you can choose to save the datasource and manually populate the Data Notes table later.</li>
                            </ul>
                          </div>
                        </div>
                        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                          <button onClick={() => setShowLocalTestHelpModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Close</button>
                        </div>
                      </div>
                    </div>
              )}

              {/* Suggested Connection String Modal */}
              {showSuggestedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recommended Connection String</h3>
                      <button onClick={() => setShowSuggestedModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
                    </div>
                    <div className="p-4 text-sm text-gray-700 dark:text-gray-300 space-y-3">
                      <p>We tested an alternative that succeeded and applied it to your input. You can copy the successful string for future reference:</p>
                      <div>
                        <div className="text-xs mb-1 text-gray-600 dark:text-gray-400">ODBC connection string</div>
                        <div className="flex items-start space-x-2">
                          <textarea readOnly value={suggestedString} className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs h-20" />
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(suggestedString); setSuggestedCopied(true); setTimeout(() => setSuggestedCopied(false), 1500); }}
                            className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                            title="Copy"
                          >
                            {suggestedCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs mb-1 text-gray-600 dark:text-gray-400">Python snippet (pyodbc)</div>
                        <div className="flex items-start space-x-2">
                          <textarea readOnly value={buildPythonSuggestedSnippet()} className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs h-24" />
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(buildPythonSuggestedSnippet()); setSuggestedCopied(true); setTimeout(() => setSuggestedCopied(false), 1500); }}
                            className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                            title="Copy"
                          >
                            {suggestedCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                      <button onClick={() => setShowSuggestedModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Close</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setStep('select')}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleTestConnection}
                  disabled={testing || !name.trim()}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {testing ? (
                    <>
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                      Testing & Saving...
                    </>
                  ) : (
                    'Save Datasource'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Connection Failure Modal */}
        {showConnectionFailureModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 mt-1">
                    <AlertCircle className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Connection Test Failed
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {connectionFailureMessage}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Would you like to save this datasource anyway? An empty Data Notes table will be created that you can populate manually.
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowConnectionFailureModal(false);
                      setSaving(false);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAnyway}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
                  >
                    Save Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDatasourceModal;
